import React, { useEffect, useState } from 'react';
import { Coins, Zap, Loader2, Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button, Badge, Spinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  getBillingAddons,
  createTopupSession,
  addRecurringAddon,
  type BillingAddon,
} from '@/services/billingService';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Format a USD-ish amount using the add-on's own currency. */
function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString()}`;
  }
}

export function BuyTokensModal({ open, onClose }: Props) {
  const { activeOrganization, userRole } = useAuth();
  const { success, error: toastError } = useToast();
  const [addons, setAddons] = useState<BillingAddon[]>([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const canBuy = userRole === 'owner' || userRole === 'admin';

  async function loadAddons() {
    if (!activeOrganization?.id) return;
    setLoading(true);
    try {
      const fetched = await getBillingAddons(activeOrganization.id);
      setAddons(fetched);
    } catch (err: any) {
      console.error('[BuyTokensModal] failed to load add-ons:', err);
      toastError(err?.message ?? 'Failed to load token packs. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open || !activeOrganization?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fetched = await getBillingAddons(activeOrganization.id);
        if (!cancelled) setAddons(fetched);
      } catch (err: any) {
        console.error('[BuyTokensModal] failed to load add-ons:', err);
        if (!cancelled) toastError(err?.message ?? 'Failed to load token packs. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, activeOrganization?.id]);

  async function handleBuy(addon: BillingAddon) {
    if (!activeOrganization?.id) return;
    setBuying(addon.id);
    try {
      const { url } = await createTopupSession(activeOrganization.id, addon.id);
      window.location.href = url;
    } catch (err: any) {
      console.error('[BuyTokensModal] top-up error:', err);
      toastError(err?.message ?? 'Failed to start checkout. Please try again.');
      setBuying(null);
    }
  }

  async function handleAddRecurring(addon: BillingAddon) {
    if (!activeOrganization?.id) return;
    setAdding(addon.id);
    try {
      await addRecurringAddon(activeOrganization.id, addon.id, 1);
      success(`"${addon.displayName}" added to your subscription`);
      await loadAddons();
    } catch (err: any) {
      console.error('[BuyTokensModal] add-recurring error:', err);
      toastError(err?.message ?? 'Failed to add to subscription. Please try again.');
    } finally {
      setAdding(null);
    }
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="Buy more tokens" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Top up your balance with one-time token packs. Purchased tokens never expire and are
          used after your monthly allowance.
        </p>

        {!canBuy ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            Only an organization owner or admin can purchase tokens. Please ask an admin to buy more
            tokens for your team.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : addons.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No token packs are available right now.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addons.map((addon) => {
              const isBuying = buying === addon.id;
              const isAdding = adding === addon.id;
              const isRecurring = addon.billing === 'recurring';
              const purchasable = !!addon.stripePriceId;

              return (
                <div
                  key={addon.id}
                  className="rounded-xl border border-border bg-card p-5 flex flex-col gap-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Coins className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-foreground leading-snug">
                        {addon.displayName}
                      </h3>
                      {typeof addon.tokens === 'number' && (
                        <Badge variant="secondary" className="mt-1.5">
                          {addon.tokens.toLocaleString()} tokens
                        </Badge>
                      )}
                    </div>
                  </div>

                  {addon.description && (
                    <p className="text-sm text-muted-foreground">{addon.description}</p>
                  )}

                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-foreground">
                      {formatPrice(addon.priceUsd, addon.currency)}
                    </span>
                    {addon.billing === 'recurring' && (
                      <span className="text-sm text-muted-foreground">/ month</span>
                    )}
                  </div>

                  <div className="mt-auto">
                    {!purchasable ? (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        Contact us to add
                      </p>
                    ) : isRecurring ? (
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        disabled={isAdding || adding !== null}
                        onClick={() => handleAddRecurring(addon)}
                      >
                        {isAdding ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Adding…
                          </>
                        ) : (
                          <>
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Add to subscription
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full"
                        disabled={isBuying || buying !== null}
                        onClick={() => handleBuy(addon)}
                      >
                        {isBuying ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Redirecting…
                          </>
                        ) : (
                          <>
                            <Zap className="w-3.5 h-3.5 mr-1.5" />
                            Buy
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
